import { computed, ref, shallowRef } from "vue";
import type { OrderFrontendService } from "../../../cbi-generator/frontend/services/OrderFrontendService";
import type { PaymentFrontendService } from "../../../cbi-generator/frontend/services/PaymentFrontendService";
import type { AddToCart } from "../../../cbi-generator/generated/dto/AddToCart";
import type { DiscountCandidate } from "../../../cbi-generator/generated/dto/DiscountCandidate";
import type { DiscountOnLines } from "../../../cbi-generator/generated/dto/DiscountOnLines";
import type { LineItemDiscountInstanceRef } from "../../../cbi-generator/generated/dto/LineItemDiscountInstanceRef";
import type { LineItemRef } from "../../../cbi-generator/generated/dto/LineItemRef";
import type { LineItemStaff } from "../../../cbi-generator/generated/dto/LineItemStaff";
import type { OrderPaymentRef } from "../../../cbi-generator/generated/dto/OrderPaymentRef";
import type { OrderPaymentStaff } from "../../../cbi-generator/generated/dto/OrderPaymentStaff";
import type { OrderRef } from "../../../cbi-generator/generated/dto/OrderRef";
import type { OrderStaff } from "../../../cbi-generator/generated/dto/OrderStaff";
import { OrderStatus } from "../../../cbi-generator/generated/dto/OrderStatus";
import type { PaymentAttempt } from "../../../cbi-generator/generated/dto/PaymentAttempt";
import { PaymentStatus } from "../../../cbi-generator/generated/dto/PaymentStatus";
import type { PersonRef } from "../../../cbi-generator/generated/dto/PersonRef";
import type { PrinterRef } from "../../../cbi-generator/generated/dto/PrinterRef";
import type { StartPayment } from "../../../cbi-generator/generated/dto/StartPayment";

export type StaffOrderServices = {
  orders: Pick<
    OrderFrontendService,
    | "openOrderStaff"
    | "changeBuyerStaff"
    | "configureReceiptDeliveryStaff"
    | "retryReceiptDeliveryStaff"
    | "getOrderStaff"
    | "getOrderStaffLineItems"
    | "getOrderStaffPayments"
    | "eligibleDiscountsStaff"
    | "reopenStaff"
    | "payStaff"
    | "cancelStaff"
    | "addToCartStaff"
    | "removeFromCartStaff"
    | "applyDiscountFromCodeStaff"
    | "applyDiscountToLineItemsStaff"
    | "unapplyDiscountFromLineItemsStaff"
    | "unapplyLineItemDiscountStaff"
  >;
  payments: Pick<
    PaymentFrontendService,
    | "startPaymentStaff"
    | "preparePaymentStaff"
    | "paymentAttemptsStaff"
    | "completePaymentStaff"
    | "cancelPaymentStaff"
    | "sendPaymentToReaderStaff"
  >;
};

const completed = new Set([
  OrderStatus.PAID,
  OrderStatus.PROCESSED,
  OrderStatus.REFUNDED,
  OrderStatus.PARTIALLY_REFUNDED,
]);

/** Owns server-backed state only; no legacy DTO conversion or client-side repricing. */
export function createStaffOrderSession(services: StaffOrderServices) {
  const orderRef = shallowRef<OrderRef | null>(null);
  const order = shallowRef<OrderStaff | null>(null);
  const lines = shallowRef<LineItemStaff[]>([]);
  const payments = shallowRef<OrderPaymentStaff[]>([]);
  const discounts = shallowRef<DiscountCandidate[]>([]);
  const attempts = shallowRef<PaymentAttempt[]>([]);
  const busy = ref(false);
  const polling = ref(false);
  let backgroundFinished: Promise<void> | null = null;
  let disposed = false;
  const isOpen = computed(() => order.value?.orderStatus === OrderStatus.OPEN);
  const isComplete = computed(
    () =>
      order.value?.orderStatus != null &&
      completed.has(order.value.orderStatus),
  );
  const dueCents = computed(() => {
    const total = order.value?.amountTotalCents;
    const paid = order.value?.amountPaidCents;
    return total == null || paid == null ? null : total - paid;
  });
  const allocatedCents = computed(() =>
    attempts.value
      .filter((attempt) =>
        [
          PaymentStatus.PENDING,
          PaymentStatus.CAPTURABLE,
          PaymentStatus.SUCCESSFUL,
          PaymentStatus.REFUNDED,
          PaymentStatus.PARTIALLY_REFUNDED,
        ].includes(attempt.status),
      )
      .reduce((sum, attempt) => sum + attempt.amountCents, 0),
  );
  const availableCents = computed(() => {
    const total = order.value?.amountTotalCents;
    return total == null ? null : total - allocatedCents.value;
  });

  function currentRef(): OrderRef {
    if (!orderRef.value) throw new Error("Open an order first.");
    return orderRef.value;
  }

  async function run<T>(action: () => Promise<T>): Promise<T> {
    if (busy.value)
      throw new Error("Wait for the current order operation to finish.");
    busy.value = true;
    try {
      await backgroundFinished;
      if (disposed) throw new Error("This shop is closed.");
      return await action();
    } finally {
      busy.value = false;
    }
  }

  async function refreshState(background = false) {
    const current = currentRef();
    const stored = await services.orders.getOrderStaff(current.assignId, {
      personId: true,
      lineItems: false,
      payments: true,
      orderRefunds: false,
    });
    // Publish the authoritative lifecycle even if a subsequent relation/lookup fails.
    if (disposed || (background && busy.value)) return;
    if (!background) order.value = stored;
    const [newLines, newPayments, newAttempts, newDiscounts] =
      await Promise.all([
        stored.lineItems ??
          services.orders.getOrderStaffLineItems(current.assignId, {
            personId: false,
            donation: true,
            membership: false,
            balanceTransaction: true,
            stockChange: false,
            card: true,
            guestPrivs: false,
            damageWaiver: false,
            purchaseDelivery: false,
            orderId: false,
            itemInstanceId: false,
            parentItemId: true,
            childItems: false,
            discountInstances: true,
            taxes: true,
            refunds: false,
          }),
        stored.payments ??
          services.orders.getOrderStaffPayments(current.assignId),
        services.payments.paymentAttemptsStaff(current),
        stored.orderStatus === OrderStatus.OPEN
          ? services.orders.eligibleDiscountsStaff(current)
          : Promise.resolve([]),
      ]);
    if (disposed || (background && busy.value)) return;
    if (background) order.value = stored;
    lines.value = newLines;
    payments.value = newPayments;
    attempts.value = newAttempts;
    discounts.value = newDiscounts;
  }

  function requireOpen() {
    if (!isOpen.value)
      throw new Error("Reopen this order before changing its cart.");
  }

  async function mutate(action: () => Promise<unknown>) {
    return run(async () => {
      requireOpen();
      await action();
      await refreshState();
    });
  }

  return {
    orderRef,
    order,
    lines,
    payments,
    discounts,
    attempts,
    busy,
    polling,
    dispose: () => {
      disposed = true;
    },
    reconcileBackground: async () => {
      if (
        disposed ||
        busy.value ||
        polling.value ||
        (order.value?.orderStatus !== OrderStatus.PAYING &&
          !(
            order.value?.orderStatus === OrderStatus.PROCESSED &&
            attempts.value.some(
              (attempt) => attempt.status === PaymentStatus.PENDING,
            )
          ))
      )
        return;
      let finished!: () => void;
      backgroundFinished = new Promise<void>((resolve) => {
        finished = resolve;
      });
      polling.value = true;
      try {
        await services.payments.completePaymentStaff(currentRef());
        await refreshState(true);
      } finally {
        polling.value = false;
        backgroundFinished = null;
        finished();
      }
    },
    isOpen,
    isComplete,
    dueCents,
    availableCents,
    open: (person: PersonRef | null, existing: OrderRef | null = null) =>
      run(async () => {
        const resume = existing ?? orderRef.value;
        if (resume) {
          const stored = await services.orders.getOrderStaff(resume.assignId);
          if (
            stored.orderStatus !== OrderStatus.OPEN &&
            stored.orderStatus !== OrderStatus.PAYING
          ) {
            orderRef.value = resume;
            await refreshState();
            return;
          }
        }
        // Retain the ref if fetching fails after creation, so retry never creates a second cart.
        orderRef.value = await services.orders.openOrderStaff({
          person,
          order: resume,
        });
        await refreshState();
        if (order.value?.orderStatus === OrderStatus.PAYING) {
          await services.orders.reopenStaff(currentRef());
          await refreshState();
        }
      }),
    refresh: () => run(refreshState),
    newSale: () => {
      if (busy.value || !isComplete.value)
        throw new Error(
          "Complete or cancel this order before starting another.",
        );
      orderRef.value = null;
      order.value = null;
      lines.value = [];
      payments.value = [];
      discounts.value = [];
      attempts.value = [];
    },
    add: (request: AddToCart) =>
      mutate(() =>
        services.orders.addToCartStaff({ order: currentRef(), request }),
      ),
    changeBuyer: (person: PersonRef) =>
      mutate(() =>
        services.orders.changeBuyerStaff({ order: currentRef(), person }),
      ),
    configureReceiptDelivery: (
      printReceipt: boolean,
      emailReceipt: boolean,
      printer: PrinterRef | null,
    ) =>
      run(async () => {
        if (
          order.value?.orderStatus !== OrderStatus.OPEN &&
          order.value?.orderStatus !== OrderStatus.PAYING
        )
          throw new Error("Receipt choices can only change before completion.");
        await services.orders.configureReceiptDeliveryStaff({
          order: currentRef(),
          printReceipt,
          emailReceipt,
          printer,
        });
        await refreshState();
      }),
    retryReceiptDelivery: () =>
      run(async () => {
        if (!isComplete.value)
          throw new Error(
            "Complete this order before retrying receipt delivery.",
          );
        await services.orders.retryReceiptDeliveryStaff(currentRef());
        await refreshState();
      }),
    remove: (line: LineItemRef) =>
      mutate(() => services.orders.removeFromCartStaff(line)),
    applyCode: (code: string) =>
      mutate(() =>
        services.orders.applyDiscountFromCodeStaff({
          order: currentRef(),
          code,
        }),
      ),
    applyDiscount: (request: DiscountOnLines) =>
      mutate(() =>
        services.orders.applyDiscountToLineItemsStaff({
          order: currentRef(),
          request,
        }),
      ),
    unapplyDiscount: (request: DiscountOnLines) =>
      mutate(() =>
        services.orders.unapplyDiscountFromLineItemsStaff({
          order: currentRef(),
          request,
        }),
      ),
    unapplyLineDiscount: (
      lineItem: LineItemRef,
      discount: LineItemDiscountInstanceRef,
    ) =>
      mutate(() =>
        services.orders.unapplyLineItemDiscountStaff({ lineItem, discount }),
      ),
    checkout: () =>
      run(async () => {
        requireOpen();
        await services.orders.payStaff(currentRef());
        await refreshState();
        await services.payments.completePaymentStaff(currentRef());
        await refreshState();
      }),
    reopen: () =>
      run(async () => {
        await services.orders.reopenStaff(currentRef());
        await refreshState();
      }),
    cancel: () =>
      run(async () => {
        await services.orders.cancelStaff(currentRef());
        orderRef.value = null;
        order.value = null;
        lines.value = [];
        payments.value = [];
        discounts.value = [];
        attempts.value = [];
      }),
    startPayment: (request: StartPayment) =>
      run(async () => {
        if (
          order.value?.orderStatus !== OrderStatus.PAYING &&
          order.value?.orderStatus !== OrderStatus.PROCESSED
        )
          throw new Error("Move the order to checkout before adding payments.");
        const payment = await services.payments.startPaymentStaff({
          order: currentRef(),
          request,
        });
        // Refresh before provider preparation so a failed prepare never hides an allocated payment.
        await refreshState();
        const attempt = await services.payments.preparePaymentStaff(payment);
        await services.payments.completePaymentStaff(currentRef());
        await refreshState();
        return attempt;
      }),
    cancelPayment: (payment: OrderPaymentRef) =>
      run(async () => {
        await services.payments.cancelPaymentStaff(payment);
        await refreshState();
      }),
    preparePayment: (payment: OrderPaymentRef) =>
      run(async () => {
        await services.payments.preparePaymentStaff(payment);
        await services.payments.completePaymentStaff(currentRef());
        await refreshState();
      }),
    sendToReader: (payment: OrderPaymentRef, readerId: string) =>
      run(() =>
        services.payments.sendPaymentToReaderStaff({ payment, readerId }),
      ),
    reconcile: () =>
      run(async () => {
        await services.payments.completePaymentStaff(currentRef());
        await refreshState();
      }),
  };
}

export type StaffOrderSession = ReturnType<typeof createStaffOrderSession>;
